/** The close checklist, from the server's reconciliation and nothing else - pure.
 *
 * **Each step is a real blocker code or the lock itself.** The server's `blockers` are five things to
 * go and do (a source that never arrived, a document still in review, an approval still open, a
 * blocking flag still open (D-20), an inventory with nothing in it) and each is a different place to go; this orders them as the work
 * runs and adds the lock as the last step. There is no step for something the server does not check
 * (for example "estimates reviewed": no blocker code stands behind it, and a tick there would be
 * invented), and no percentage.
 *
 * A step is `done` when its blocker is absent, `todo` when it is present, and the lock is `current`
 * only when every step before it is done and the period is open. A period that is already locked
 * reads every step as done: the close cleared them when it happened, and today's blockers about a
 * closed period are not a reason to re-list them.
 */

import type { Reconciliation } from "../api/queries";

export type CloseStepState = "done" | "todo" | "current" | "ahead";

export interface CloseStep {
  key:
    | "source_gap"
    | "documents_in_review"
    | "approval_pending"
    | "blocking_flag_open"
    | "nothing_recorded"
    | "lock";
  label: string;
  state: CloseStepState;
  /** What the server said about it, or what held. Never invented. */
  detail: string;
  remedy?: string;
  count?: number;
  /** For an approval or a blocking flag: the record whose workflow holds it. */
  recordId?: string | null;
  /** Where to go and do it. */
  to?: { label: string; to: string };
}

/** A response is checked, not trusted: an array or an object without blockers is a server (or a
 * proxy) answering with something else, and it is a failed read, never "nothing outstanding". */
export function isReconciliation(value: unknown): value is Reconciliation {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Array.isArray((value as { blockers?: unknown }).blockers) &&
    typeof (value as { status?: unknown }).status === "string"
  );
}

const PLACE: Record<string, { label: string; to: string } | undefined> = {
  source_gap: { label: "Open missing bills", to: "/sources" },
  documents_in_review: { label: "Open the documents", to: "/evidence" },
  nothing_recorded: { label: "Open the figures", to: "/emissions" },
};

const ORDER: Array<{ key: CloseStep["key"]; label: string }> = [
  { key: "source_gap", label: "Every expected source is accounted for" },
  { key: "documents_in_review", label: "Every document is read and decided" },
  { key: "approval_pending", label: "No approval request is waiting" },
  { key: "blocking_flag_open", label: "No blocking flag is open" },
  { key: "nothing_recorded", label: "Something is recorded for the period" },
];

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** **Windows over windows** (G-Wauditor-01). The close once printed "263 of 27 expected sources
 * accounted for": month windows that arrived over the number of expected-source rows. A fraction
 * counts one unit, so this prints the server's `source_periods_accounted` of `source_periods_due`
 * and never `sources_submitted` over `sources_expected`. A response whose numerator exceeds its
 * denominator is a wrong read, not a fraction: it prints the count alone rather than "X of Y" with
 * X > Y. */
export function sourcePeriodsLine(rec: Reconciliation): string {
  const accounted = rec.source_periods_accounted;
  const due = rec.source_periods_due;
  if (accounted > due) return `${plural(accounted, "source period", "source periods")} accounted for`;
  return `${accounted} of ${plural(due, "expected source period", "expected source periods")} accounted for`;
}

/** What the figure count counts. `figures` is every current calculation row, which includes the
 * market-based Scope 2 row beside the location-based one, so "441 figures" sat beside a Scope 1
 * and 2 page showing 303 (G-Wauditor-01). This states the figures behind the totals and, when
 * there are any, the market-basis rows beside them. `fmt` lets a screen group digits. */
export function figuresLine(rec: Reconciliation, fmt: (n: number) => string = String): string {
  const base = `${fmt(rec.non_market_figures)} ${rec.non_market_figures === 1 ? "figure" : "figures"}`;
  if (rec.market_based_figures <= 0) return base;
  return `${base} and ${fmt(rec.market_based_figures)} market-based Scope 2 ${rec.market_based_figures === 1 ? "row" : "rows"}`;
}

export function closeSteps(rec: Reconciliation): CloseStep[] {
  const locked = rec.status === "locked";
  const steps: CloseStep[] = ORDER.map(({ key, label }) => {
    const blocker = rec.blockers.find((entry) => entry.code === key);
    if (blocker && !locked) {
      return {
        key,
        label,
        state: "todo",
        detail: blocker.message,
        remedy: blocker.remedy,
        count: blocker.count,
        recordId: blocker.record_id,
        to: PLACE[key],
      };
    }
    const held: Record<string, string> = {
      source_gap: `${sourcePeriodsLine(rec)}${rec.declared_nils > 0 ? `, ${rec.declared_nils} confirmed none` : ""}.`,
      documents_in_review: "No document for this period is waiting.",
      approval_pending: "No approval is waiting on a reviewer.",
      blocking_flag_open: "No blocking flag is open on this company's records.",
      nothing_recorded: `${figuresLine(rec)} recorded.`,
    };
    return { key, label, state: "done", detail: held[key] ?? "" };
  });
  const before = steps.every((step) => step.state === "done");
  const lock: CloseStep = locked
    ? { key: "lock", label: "The period is closed", state: "done", detail: "Closed." }
    : before
      ? { key: "lock", label: "Close the period", state: "current", detail: "Everything above holds." }
      : {
          key: "lock",
          label: "Close the period",
          state: "ahead",
          detail: `Waits for ${plural(steps.filter((step) => step.state === "todo").length, "step", "steps")} above.`,
        };
  return [...steps, lock];
}

/** A period as the person picks it and as the close drawer is titled: the regime, then the year. One
 * spelling, so the filter line and the act agree on which period is meant. */
export function periodName(period: { regime_key: string; label: string }): string {
  return `${period.regime_key.replace(/_/g, " ")}, ${period.label}`;
}

/** The periods a workspace holds, with the one the shell's window names first. A window can belong
 * to two regimes with different year ends, so the match is on both dates, and a period no window
 * names still appears. */
export function orderPeriods<T extends { start_date: string; end_date: string; regime_key: string; label: string }>(
  periods: readonly T[],
  window: { period_start: string; period_end: string } | undefined,
): T[] {
  const named = (period: T) =>
    window !== undefined && period.start_date === window.period_start && period.end_date === window.period_end;
  return [...periods].sort((a, b) => {
    const first = Number(named(b)) - Number(named(a));
    return first || b.end_date.localeCompare(a.end_date) || a.regime_key.localeCompare(b.regime_key);
  });
}
